/**
 * The request-sent page's behavior half: the request read on the website
 * session, its rows, Open chat when Telegram would not deliver its message,
 * the calendar file, and Cancel request. Draws nothing.
 */
import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { requestSentLine } from '@/pages/booking/shared/bookingRules';
import type { BookingDetail } from '@/types/bookingDetail';
import { CALENDAR_FILE_FAILED, bookingCalendarLinks } from '@/utils/calendarLinks';

import { CLIENT_SCOPE, webFetch } from '../../api/session';
import { useSiteStudio } from '../../api/site';
import { usePieceEmit, usePieceOptions, type PieceInput, type PieceRuntime } from '../../contract/runtime';
import { getClientTimezone } from '../../format';
import { bookedRows, openCalendarFile, telegramLine, useWebBooking } from '../../hooks/useWebBooking';
import { bookingPhoto } from '../../ui/Photo';
import { contract, type RequestSentActions, type RequestSentEvents, type RequestSentState } from './contract';

export type RequestSentRuntime = PieceRuntime<typeof contract, RequestSentState, RequestSentActions>;

export function useRequestSent(input: PieceInput<RequestSentEvents> = {}): RequestSentRuntime {
  const options = usePieceOptions(contract, input.options);
  const emit = usePieceEmit(input.onEvent);
  const queryClient = useQueryClient();
  const { bookingId = '' } = useParams<{ bookingId: string }>();
  const { detail, message } = useWebBooking(bookingId);
  const studio = useSiteStudio().data;
  const tz = useMemo(() => getClientTimezone(), []);
  const b = detail.data;
  const [cancelling, setCancelling] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const cancel = useCallback(() => {
    if (cancelling) return;
    setCancelling(true);
    setProblem(null);
    webFetch<{ booking: BookingDetail }>(`/booking-hub/booking/${encodeURIComponent(bookingId)}/cancel`, { method: 'POST' })
      .then((res) => {
        queryClient.setQueryData([...CLIENT_SCOPE, 'booking', bookingId], res.booking);
        emit('cancelled', {});
      })
      .catch(() => {
        setProblem("Couldn't cancel your request. Please try again.");
        void queryClient.invalidateQueries({ queryKey: [...CLIENT_SCOPE, 'booking', bookingId] });
      })
      .finally(() => setCancelling(false));
  }, [cancelling, bookingId, queryClient, emit]);

  const actions = useMemo<RequestSentActions>(
    () => ({
      addToCalendar: () => {
        if (b) openCalendarFile(b).catch(() => setProblem(CALENDAR_FILE_FAILED));
      },
      cancel,
    }),
    [b, cancel],
  );

  const cancelled = b?.status === 'CANCELLED';
  const atSession = b?.paymentRequirement === 'PAY_AT_SESSION' && Boolean(b?.payment?.amount);
  const lead = cancelled ? 'This request is no longer active. Nothing was charged.' : requestSentLine(studio?.name ?? '', atSession);

  return {
    state: {
      status: detail.isError ? 'not-found' : b ? 'ready' : 'loading',
      cancelled,
      lead,
      name: b?.service.name ?? '',
      photoUrl: b?.service.imageUrl ?? null,
      photo: b ? bookingPhoto(b.service) : null,
      withName: b?.provider?.name ? `with ${b.provider.name}` : null,
      rows: b ? bookedRows(b, tz, true) : [],
      canCancel: Boolean(b?.canCancel) && b?.status === 'PENDING_APPROVAL',
      cancelling,
      problem,
      calendar: b ? bookingCalendarLinks(b) : null,
      telegram: cancelled ? null : telegramLine(message, studio, true),
      bookingHref: `/account/bookings/${encodeURIComponent(bookingId)}`,
    },
    actions,
    options,
  };
}
